import * as React from 'react';
import * as styles from './styles.scss';
import {
    focusZoomTools,
    blurZoomTools,
    expandZoomTools,
    collapseZoomTools,
    getCanvasBounds,
    getShowExpandedZoomTools,
    isMovingAssets,
    isPanning,
} from '../viewstate';
import { CommandBar, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { getTheme } from '@fluentui/react/lib/Styling';
import { IButtonProps, CommandBarButton } from '@fluentui/react/lib/Button';
import type { IContextualMenuItemStyles } from '@fluentui/react/lib/ContextualMenu';
import { observer } from 'mobx-react-lite';
import useActivityPaneContext from '../hooks/useActivityPaneContext';
import { zoomRegionName } from './TimeStreamZoomTools.locstring.json';
import classNames from 'classnames';
import getZoomItems from '../util/getZoomItems';
import SkipLinkRegionToFirstFocusableChild from 'owa-skip-link/lib/components/SkipLinkRegionToFirstFocusableChild';
import TimeStreamMiniMap from './TimeStreamMiniMap';

const commandBarStyles: ICommandBarStyles = {
    root: { padding: 0 },
    primarySet: { padding: 0 },
};

const CustomButton: React.FunctionComponent<IButtonProps> = props => {
    const neutralSecondaryColor = { color: getTheme().palette.neutralSecondary };
    const itemStyles: Partial<IContextualMenuItemStyles> = {
        root: { ...neutralSecondaryColor, outlineOffset: '-4px', height: 'auto' },
        iconHovered: neutralSecondaryColor,
        iconPressed: neutralSecondaryColor,
        icon: neutralSecondaryColor,
    };
    return <CommandBarButton {...props} styles={itemStyles} />;
};

// Style to avoid collision with right toolbox.
const avoidCollisionStyle: React.CSSProperties = {
    right: 60,
};

export default observer(function TimeStreamZoomTools() {
    const isExpanded = getShowExpandedZoomTools();
    const { ActivityPane } = useActivityPaneContext();
    const { height } = getCanvasBounds();
    const style = !!ActivityPane && height <= 285 ? avoidCollisionStyle : undefined;

    const className = classNames(styles.zoomTools, {
        [styles.zoomToolsExpanded]: isExpanded,
    });

    return (
        <SkipLinkRegionToFirstFocusableChild
            role="complementary"
            regionNameResourceId={zoomRegionName}
            className={styles.zoomToolsContainer}
            style={style}>
            <TimeStreamMiniMap />
            <CommandBar
                className={className}
                styles={commandBarStyles}
                onPointerEnter={expandZoomToolsIfNotDraggingOrPanning}
                onPointerLeave={collapseZoomTools}
                onFocus={focusZoomTools}
                onBlur={blurZoomTools}
                items={getZoomItems(isExpanded)}
                onReduceData={dontLetCommandBarCollapse}
                buttonAs={CustomButton}
            />
        </SkipLinkRegionToFirstFocusableChild>
    );
});

function expandZoomToolsIfNotDraggingOrPanning() {
    if (!(isMovingAssets() || isPanning())) {
        expandZoomTools();
    }
}

function dontLetCommandBarCollapse() {
    return undefined;
}
